import { z } from 'zod';
import type { MatchDiagnosticBundle } from '../diagnostics/analyzer';
import { normalizeDiagnosticBundle } from '../diagnostics/compact';
import { DECK_SIZE, isValidDeckComposition } from '../engine/deck-rules';
import type { ReplayFile } from './replay';

const playerIdSchema = z.enum(['player', 'bot']);
const pointSchema = z.object({ x: z.number().finite(), y: z.number().finite() });
const resultSchema = z.object({
  winner: z.union([playerIdSchema, z.literal('draw')]),
  reason: z.enum(['king-destroyed', 'time', 'sudden-death', 'tiebreak']),
  crowns: z.object({ player: z.number().int().nonnegative(), bot: z.number().int().nonnegative() }),
  tick: z.number().int().nonnegative(),
});
const rulesSchema = z.object({
  id: z.string().min(1), tickRate: z.number().int().positive(), countdownTicks: z.number().int().nonnegative(),
  normalTicks: z.number().int().nonnegative(), overtimeTicks: z.number().int().nonnegative(), startingElixirMilli: z.number().int().nonnegative(),
  maxElixirMilli: z.number().int().positive(),
  singleElixirPerTickMilli: z.number().int().nonnegative(), doubleElixirPerTickMilli: z.number().int().nonnegative(), tripleElixirPerTickMilli: z.number().int().nonnegative(),
  normalDoubleElixirStartTick: z.number().int().nonnegative(), overtimeTripleElixirStartTick: z.number().int().nonnegative(),
  singleCardCooldownTicks: z.number().int().nonnegative(), doubleCardCooldownTicks: z.number().int().nonnegative(), tripleCardCooldownTicks: z.number().int().nonnegative(),
});
const commandSchema = z.object({
  type: z.literal('play-card'), tick: z.number().int().nonnegative(), sequence: z.number().int().positive(),
  playerId: playerIdSchema, cardId: z.string().min(1), position: pointSchema,
});
const deckSchema = z.array(z.string().min(1))
  .length(DECK_SIZE)
  .refine(isValidDeckComposition, `Deck must contain exactly ${DECK_SIZE} unique cards`);

const styleSchema = z.object({
  aggression: z.number().int().min(0).max(1_000), cycle: z.number().int().min(0).max(1_000), beatdown: z.number().int().min(0).max(1_000),
  airPressure: z.number().int().min(0).max(1_000), swarm: z.number().int().min(0).max(1_000), spellControl: z.number().int().min(0).max(1_000),
  defense: z.number().int().min(0).max(1_000), lanePreference: z.number().int().min(-1_000).max(1_000),
});
const botStrategySchema = z.object({
  schemaVersion: z.literal(1), botId: z.string().min(1), botVersion: z.string().min(1), deckPolicyId: z.string().min(1), deckPolicyVersion: z.string().min(1),
  modelVersion: z.string().min(1).nullable(), modelHash: z.string().min(1), confidence: z.number().int().min(0).max(1_000), sourceMatches: z.number().int().nonnegative(),
  style: styleSchema, selectedDeck: deckSchema, deckScore: z.number().finite(), rationale: z.array(z.string().min(1)),
});
const replayBaseSchema = z.object({
  engineVersion: z.string().min(1), contentVersion: z.string().min(1), seed: z.number().int().nonnegative(),
  rulesetId: z.string().min(1), rules: rulesSchema, arenaId: z.string().min(1),
  decks: z.object({ player: deckSchema, bot: deckSchema }),
  commands: z.array(commandSchema), checkpoints: z.array(z.object({ tick: z.number().int().nonnegative(), checksum: z.string().min(1) })),
  result: resultSchema.nullable(),
});
const currentReplaySchema = replayBaseSchema.extend({ formatVersion: z.literal(2), botStrategy: botStrategySchema.optional() });
const previousReplaySchema = replayBaseSchema.extend({ formatVersion: z.literal(1) }).transform((replay) => ({ ...replay, formatVersion: 2 as const }));
export const replayFileSchema = z.union([currentReplaySchema, previousReplaySchema]);

const diagnosticBundleSchema = z.object({
  manifest: z.object({ formatVersion: z.literal(1), matchId: z.string().min(1), createdAt: z.string().min(1), engineVersion: z.string().min(1), contentVersion: z.string().min(1) }).passthrough(),
  replay: replayFileSchema,
  records: z.array(z.object({ schemaVersion: z.literal(1), matchId: z.string().min(1), eventId: z.number().int().positive(), tick: z.number().int().nonnegative(), phase: z.string(), source: z.string(), level: z.string(), code: z.string(), payload: z.unknown() }).passthrough()),
  checkpoints: z.array(z.object({ tick: z.number().int().nonnegative(), checksum: z.string().min(1) })),
  metrics: z.object({ records: z.number().int().nonnegative() }).passthrough(),
  findings: z.array(z.object({ ruleId: z.string(), severity: z.string(), tickFrom: z.number(), tickTo: z.number() }).passthrough()),
  summary: z.object({ result: resultSchema.nullable(), critical: z.number().int().nonnegative(), errors: z.number().int().nonnegative(), warnings: z.number().int().nonnegative() }),
});

export function parseReplayFile(value: unknown): ReplayFile {
  return replayFileSchema.parse(value) as unknown as ReplayFile;
}

export function parseDiagnosticBundle(value: unknown): MatchDiagnosticBundle {
  return diagnosticBundleSchema.parse(normalizeDiagnosticBundle(value)) as unknown as MatchDiagnosticBundle;
}
